import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Trash2, NotebookPen, Plus, X } from 'lucide-react';

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [filter, setFilter] = useState('');

  const load = async () => {
    const { data } = await api.get('/journal');
    setEntries(data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newContent.trim()) return;
    await api.post('/journal', { content: newContent });
    setNewContent('');
    setShowNew(false);
    // XP awarded server-side; nudge XpBar + show small toast.
    window.dispatchEvent(new Event('kc:xp-updated'));
    toast.success('+5 XP', {
      description: 'Journal entry saved.',
      duration: 2800,
      style: { background: '#0B0B0C', color: '#F0C660', border: '1px solid rgba(212,164,55,0.4)' },
    });
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await api.delete(`/journal/${id}`);
    load();
  };

  const filtered = entries.filter((e) => !filter || (e.content || '').toLowerCase().includes(filter.toLowerCase()) || (e.lesson_title || '').toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="kc-container py-8 md:py-12">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc flex items-center gap-1"><NotebookPen size={14}/> Personal Journal</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-1">Your <span className="text-gold">reflections.</span></h1>
        </div>
        <button onClick={() => setShowNew(true)} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="journal-new"><Plus size={16}/> New entry</button>
      </div>

      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search your reflections…" className="w-full md:max-w-md h-11 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="journal-search" />

      <div className="mt-6 space-y-3" data-testid="journal-list">
        {filtered.length === 0 && <div className="text-muted-kc text-sm">No entries yet. Start with today's reflection.</div>}
        {filtered.map((e) => (
          <div key={e.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5" data-testid={`journal-entry-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                {e.lesson_title && <div className="text-[11px] tracking-[0.18em] uppercase text-gold">{e.lesson_title}</div>}
                {e.prompt && <div className="font-reader italic text-ink-2 text-sm mt-1">{e.prompt}</div>}
                <div className="text-xs text-muted-kc mt-1">{new Date(e.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>
              <button onClick={() => del(e.id)} className="p-2 text-muted-kc hover:text-[color:var(--kc-danger)] transition-colors" data-testid={`journal-del-${e.id}`}><Trash2 size={16}/></button>
            </div>
            <div className="mt-3 font-reader text-base text-ink-2 whitespace-pre-wrap leading-relaxed">{e.content}</div>
          </div>
        ))}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="journal-new-modal">
          <div className="bg-white rounded-[var(--kc-radius-md)] p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display uppercase text-2xl">New journal entry</div>
              <button onClick={() => setShowNew(false)} className="p-1 text-muted-kc" data-testid="journal-modal-close"><X size={18}/></button>
            </div>
            <textarea autoFocus value={newContent} onChange={(e) => setNewContent(e.target.value)} rows={10} placeholder="Take your time. No one sees this but you." className="w-full p-4 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)] font-reader text-base leading-relaxed" data-testid="journal-modal-textarea" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="h-11 px-4 rounded-[var(--kc-radius-sm)] hover:bg-surface-2 transition-colors" data-testid="journal-modal-cancel">Cancel</button>
              <button onClick={create} className="h-11 px-5 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="journal-modal-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
